import { ReactNode } from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { cx } from './cx';
import { typography } from './Text';

export type ButtonVariant =
  | 'primary'
  | 'success'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

const base =
  'appearance-none bg-transparent border border-transparent inline-flex items-center justify-center gap-1.5 ' +
  `whitespace-nowrap rounded-lg ${typography.button} outline-none transition duration-100 cursor-pointer select-none ` +
  'focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3',
  md: 'h-10 px-3.5',
  lg: `h-11 px-4 ${typography.buttonLg}`,
};

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-solid text-on-brand shadow-xs hover:bg-brand-solid-hover',
  success: 'bg-success-600 text-base-white shadow-xs hover:bg-success-600/90',
  secondary:
    'bg-surface text-fg-secondary border border-strong shadow-xs hover:bg-muted-strong',
  tertiary: 'bg-neutral text-fg-secondary hover:bg-neutral-hover',
  ghost: 'text-fg-secondary hover:bg-neutral',
  destructive: 'bg-error-600 text-base-white shadow-xs hover:bg-error-700',
};

export interface ButtonProps extends Omit<
  AriaButtonProps,
  'children' | 'className'
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeading?: ReactNode;
  iconTrailing?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * The Regask button — react-aria `Button` (keyboard + ARIA + press states)
 * styled in the Untitled UI idiom. `primary` is the brand fill, `secondary`
 * the bordered default with an xs shadow, `tertiary` the quiet action,
 * `destructive` for confirms.
 */
export const Button = ({
  variant = 'secondary',
  size = 'md',
  iconLeading,
  iconTrailing,
  fullWidth = false,
  className,
  children,
  ...props
}: ButtonProps) => (
  <AriaButton
    {...props}
    className={cx(
      base,
      sizes[size],
      variants[variant],
      fullWidth && 'w-full',
      className,
    )}
  >
    {iconLeading && <span className="inline-flex shrink-0">{iconLeading}</span>}
    {children}
    {iconTrailing && (
      <span className="inline-flex shrink-0">{iconTrailing}</span>
    )}
  </AriaButton>
);
